import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HeroSection from '../components/common/HeroSection';
import SectionTitle from '../components/common/SectionTitle';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import DiversityIcon from '@mui/icons-material/Diversity3';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const values = [
  { icon: <FavoriteIcon sx={{ fontSize: 36 }} />, title: 'Passion', description: 'We teach because we love the Chinese language and believe every child can excel.' },
  { icon: <AutoStoriesIcon sx={{ fontSize: 36 }} />, title: 'Quality', description: 'Carefully structured curriculum that covers listening, reading, writing, and oral skills.' },
  { icon: <DiversityIcon sx={{ fontSize: 36 }} />, title: 'Community', description: 'We build a supportive learning community where students and parents feel welcome.' },
  { icon: <HistoryEduIcon sx={{ fontSize: 36 }} />, title: 'Tradition', description: 'Honouring Chinese culture and language heritage while using modern teaching methods.' },
];

const AboutPage = () => {
  return (
    <Box>
      <HeroSection
        title="About Us"
        subtitle="Nurturing Chinese language excellence since 2003"
      />

      {/* Our Story */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionTitle
            title="Our Story"
            subtitle="Two decades of dedicated Chinese language education"
          />
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}>
                Xing Fan Learning Centre (星帆补习中心) was founded by Mrs Shao Xinmo with a simple
                mission: to help students in Singapore develop a strong command of the Chinese language.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}>
                With over 20 years of teaching experience, Mrs Shao has guided hundreds of students
                from Kindergarten to Secondary school through their Chinese language journey. Her
                patient and structured teaching approach ensures that every student, regardless of
                their starting level, can build confidence and achieve results.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                Located at Canberra Community Club in Sembawang, our centre provides a warm,
                encouraging environment where students thrive. We believe that learning Chinese
                should be an enjoyable and rewarding experience.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={process.env.PUBLIC_URL + '/images/company-logo.png'}
                alt="Xing Fan Learning Centre"
                sx={{
                  width: '100%',
                  maxWidth: 350,
                  display: 'block',
                  mx: 'auto',
                  borderRadius: 4,
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Values */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <SectionTitle title="Our Values" subtitle="What drives us every day" />
          <Grid container spacing={3}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      bgcolor: 'secondary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Location */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionTitle title="Our Location" subtitle="Visit us at Canberra Community Club" />
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                  <LocationOnIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Canberra Community Club
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                      2 Sembawang Crescent #03-03C<br />
                      Singapore 757632
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Conveniently located near Canberra MRT station, with ample parking available at
                  the community club.
                </Typography>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/tutors"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Meet Our Tutors
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  height: { xs: 300, md: '100%' },
                  minHeight: 300,
                }}
              >
                <iframe
                  title="Canberra Community Club Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.5835!2d103.8270!3d1.4450!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da145a5f53a0c1%3A0x5c0e3c5e5e5e5e5e!2sCanberra%20Community%20Club!5e0!3m2!1sen!2ssg!4v1700000000000!5m2!1sen!2ssg"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: 300 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
